import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import { reloadJobs } from 'common/actions/jobs';
import AJAX from 'common/AJAX';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { RouterContext } from 'common/containers/RouterContainer';
import connect from 'common/core/connect';
import Dropdown from 'common/Dropdown';
import AutoResizeTextarea from 'common/inputs/AutoResizeTextarea';
import Button from 'common/inputs/Button';
import CheckboxInput from 'common/inputs/CheckboxInput';
import TextInput from 'common/inputs/TextInput';
import InputModal from 'common/modals/InputModal';
import UppercaseHeader from 'common/UppercaseHeader';
import parseJSON from 'common/util/parseJSON';
import withContexts from 'common/util/withContexts';

import 'css/components/jobs/_CreateJobForm.scss';

const NewDepartmentID = '__new__';

function getDepartments(jobs) {
  const departmentSet = {};
  jobs.forEach((job) => {
    departmentSet[job.department] = true;
  });
  return Object.keys(departmentSet);
}

class CreateJobForm extends Component {
  static propTypes = {
    openModal: PropTypes.func,
    router: PropTypes.object,
  };

  state = {
    error: null,
    newDepartment: null,
    saving: false,
    sections: this.props.job ? this.props.job.sections : [],
  };

  constructor(props, context) {
    super(props, context);

    this.departmentDropdownRef = React.createRef();
    this.nameRef = React.createRef();
    this.remoteRef = React.createRef();
  }

  onAddSection = () => {
    const { sections } = this.state;
    this.setState({
      sections: sections.slice(0).concat({
        details: '',
        name: 'New Section',
      }),
    });
  };

  onDeleteSection = (index) => {
    const { sections } = this.state;
    this.setState({
      sections: sections.filter((section, i) => {
        return i !== index;
      }),
    });
  };

  onDepartmentSelected = (departmentID) => {
    if (departmentID !== NewDepartmentID) {
      return;
    }

    const { openModal } = this.props;
    openModal(InputModal, {
      inset: 'Department Name',
      onCancel: () => {
        this.departmentDropdownRef.current.onChoose(null);
      },
      onValue: (value) => {
        this.setState(
          {
            newDepartment: value,
          },
          () => {
            const { jobs } = this.props;
            const departments = getDepartments(jobs);
            this.departmentDropdownRef.current.onChoose(departments.length);
          },
        );
      },
      placeholder: 'Marketing',
    });
  };

  onSectionNameChange = (index, e) => {
    const { sections } = this.state;
    const newSections = sections.slice(0);
    newSections[index].name = e.target.value;
    this.setState({
      sections: newSections,
    });
  };

  onSectionDetailsChange = (index, e) => {
    const { sections } = this.state;
    const newSections = sections.slice(0);
    newSections[index].details = e.target.value;
    this.setState({
      sections: newSections,
    });
  };

  onSubmit = () => {
    const department = this.departmentDropdownRef.current.getValue();
    const name = this.nameRef.current.getValue();
    const remote = this.remoteRef.current.getValue();
    const { sections } = this.state;

    if (!department || !name | !sections || !sections.length) {
      this.setState({
        error: 'Fill out the form!',
      });
      return;
    }

    this.setState({
      saving: true,
    });

    const { job, router } = this.props;
    if (job) {
      AJAX.post(
        '/api/jobs/edit',
        {
          department,
          jobID: job._id,
          name,
          remote,
          sections,
        },
        (response) => {
          const { reloadJobs } = this.props;
          reloadJobs().then(() => {
            parseJSON(response, (error, data) => {
              if (error || !data || !data.job) {
                router.push('/');
                return;
              }

              const { job } = data;
              const departmentURL = job.department.toLowerCase().replace(/\s/g, '-');
              router.push('/' + departmentURL + '/' + job.urlName);
            });
          });
        },
      );
    } else {
      AJAX.post(
        '/api/jobs/create',
        {
          department,
          name,
          remote,
          sections,
        },
        (response) => {
          const { reloadJobs } = this.props;
          reloadJobs().then(() => {
            parseJSON(response, (error, data) => {
              if (error || !data || !data.job) {
                router.push('/');
                return;
              }

              const { job } = data;
              const departmentURL = job.department.toLowerCase().replace(/\s/g, '-');
              router.push('/' + departmentURL + '/' + job.urlName);
            });
          });
        },
      );
    }
  };

  renderDepartmentDropdown() {
    const { job, jobs } = this.props;
    const departments = getDepartments(jobs);

    const additionalOptions = [];
    const { newDepartment } = this.state;
    if (newDepartment) {
      additionalOptions.push({
        name: newDepartment,
        render: newDepartment,
      });
    }
    additionalOptions.push({
      name: NewDepartmentID,
      render: 'Create New Department',
    });
    return (
      <div className="department">
        <UppercaseHeader>Department</UppercaseHeader>
        <Dropdown
          defaultSelectedName={job ? job.department : null}
          key={newDepartment}
          onChange={this.onDepartmentSelected}
          options={departments
            .map((department) => ({
              name: department,
              render: department,
            }))
            .concat(additionalOptions)}
          ref={this.departmentDropdownRef}
        />
      </div>
    );
  }

  renderError() {
    const { error } = this.state;
    if (!error) {
      return null;
    }

    return <div className="error">{error}</div>;
  }

  renderSections() {
    const { sections } = this.state;
    const items = sections.map((section, i) => {
      return (
        <div className="section" key={i}>
          <TextInput
            inset="Name"
            onChange={this.onSectionNameChange.bind(this, i)}
            placeholder="Requirements"
            value={section.name}
          />
          <AutoResizeTextarea
            inset="Details"
            minRows={3}
            onChange={this.onSectionDetailsChange.bind(this, i)}
            placeholder="You gotta be awesome!"
            value={section.details}
          />
          <Button onTap={this.onDeleteSection.bind(this, i)} value="Delete Section" />
        </div>
      );
    });
    return <div className="sections">{items}</div>;
  }

  render() {
    const { saving } = this.state;
    const { job } = this.props;
    return (
      <div className="createJobForm">
        {this.renderDepartmentDropdown()}
        <TextInput
          defaultValue={job ? job.name : ''}
          inset="Name"
          placeholder="Chief Fun Officer"
          ref={this.nameRef}
        />
        <div className="checkboxes">
          <CheckboxInput
            defaultChecked={job ? job.remote : true}
            label="Remote"
            ref={this.remoteRef}
          />
        </div>
        {this.renderSections()}
        <Button onTap={this.onAddSection} value="Add Section" />
        {this.renderError()}
        <Button loading={saving} onTap={this.onSubmit} value={job ? 'Edit Job' : 'Create Job'} />
      </div>
    );
  }
}

export default compose(
  connect(null, { reloadJobs }),
  withContexts(
    {
      openModal: OpenModalContext,
      router: RouterContext,
    },
    {
      forwardRef: true,
    },
  ),
)(CreateJobForm);
